
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ICONS } from '../constants';

interface LegalModalProps {
  type: 'TERMS' | 'PRIVACY' | null;
  onClose: () => void;
}

export const LegalModal: React.FC<LegalModalProps> = ({ type, onClose }) => {
  if (!type) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="w-full max-w-2xl bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[85vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/5 bg-zinc-900/50">
            <h2 className="text-xl font-bold uppercase tracking-widest text-white">
              {type === 'TERMS' ? 'Terms of Service' : 'Privacy Policy'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full text-zinc-400 hover:text-white transition-colors"
            >
              <ICONS.Close className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto p-6 md:p-10 text-zinc-400 text-xs md:text-sm leading-relaxed space-y-6 scrollbar-hide">
            
            {type === 'TERMS' && (
              <>
                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">1. Acceptance of Terms</h3>
                    <p>By accessing and using RESHOOTER ("the Service"), you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the Service.</p>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">2. AI Generation Disclaimer</h3>
                    <p>The Service utilizes artificial intelligence (Google Gemini API) to transform images. You acknowledge that:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>AI outputs are probabilistic and may contain artifacts, hallucinations, or inaccuracies.</li>
                        <li>We do not guarantee that the generated images will meet your specific requirements.</li>
                        <li>You are responsible for reviewing all outputs before using them for commercial purposes.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">3. User Content & Conduct</h3>
                    <p>You retain ownership of the original images you upload ("User Content"). By uploading content, you grant us a license to process it solely for the purpose of generating your results.</p>
                    <p className="mt-2 text-red-400"><strong>Strictly Prohibited:</strong> You agree NOT to upload or generate:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1 text-zinc-300">
                        <li>Child Sexual Abuse Material (CSAM). We report all instances to NCMEC.</li>
                        <li>Non-consensual sexual content (deepfakes).</li>
                        <li>Content that promotes violence, hate speech, or illegal acts.</li>
                        <li>Copyrighted material for which you do not have the rights.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">4. Commercial Rights</h3>
                    <p><strong>Free Plan:</strong> Personal use only.</p>
                    <p><strong>Basic & Pro Plans:</strong> You are granted a commercial license for the images you generate, subject to the AI provider's terms and applicable laws.</p>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">5. Limitation of Liability</h3>
                    <p>In no event shall RESHOOTER, its directors, employees, or partners, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of the Service.</p>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">6. Refunds & Cancellations</h3>
                    <p>Due to the high computational costs of AI generation, we generally do not offer refunds for RESHOOTS used. Subscription cancellations will prevent future billing but do not refund the current period.</p>
                </section>
              </>
            )}

            {type === 'PRIVACY' && (
              <>
                 <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">1. Information We Collect</h3>
                    <p>We collect information you provide directly to us:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Account data (Name, Email) via Google/Email authentication.</li>
                        <li>User Content (Images uploaded for processing).</li>
                        <li>Usage data (Interaction logs, error reports).</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">2. How We Use Your Data</h3>
                    <p>Your uploaded images are processed temporarily to generate your results. We store your history (original and generated images) in secure cloud storage (Firebase) linked to your account ID.</p>
                    <p className="mt-2"><strong>We do NOT use your private uploads to train our public AI models.</strong></p>
                </section>

                 <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">3. Third-Party Services</h3>
                    <p>We share data with trusted third-party providers to facilitate the Service:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li><strong>Google Cloud / Firebase:</strong> Authentication, Database, and Storage.</li>
                        <li><strong>Google Gemini API:</strong> AI Image Generation.</li>
                        <li><strong>Stripe:</strong> Payment processing. We do not store credit card details.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-white font-bold uppercase tracking-wider mb-2">4. Data Retention</h3>
                    <p>We retain your image history for your convenience until you delete it. You may delete your account and all associated data at any time via the Profile settings.</p>
                </section>
              </>
            )}

          </div>

          <div className="p-6 border-t border-white/5 bg-zinc-900/50 flex justify-end">
             <button 
                onClick={onClose}
                className="bg-white text-black px-6 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-200"
            >
                Understood
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
